import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { resolveImageUrl } from '../../utils/formatters';

const UserProfile = () => {
  const { user, updateProfile } = useAuth();
  const browserTimeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    []
  );

  const [saving, setSaving] = useState(false);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    timezone: browserTimeZone
  });

  useEffect(() => {
    if (!user) return;
    setFormData({
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      phone: user.phone || '',
      timezone: user.timezone || browserTimeZone
    });
    setProfileImagePreview(user.profile_image ? resolveImageUrl(user.profile_image) : null);
  }, [user, browserTimeZone]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setProfileImageFile(file);
    if (!file) {
      setProfileImagePreview(user?.profile_image ? resolveImageUrl(user.profile_image) : null);
      return;
    }
    const nextPreview = URL.createObjectURL(file);
    setProfileImagePreview(nextPreview);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = new FormData();
      payload.append('first_name', formData.first_name);
      payload.append('last_name', formData.last_name);
      payload.append('phone', formData.phone);
      payload.append('timezone', formData.timezone || browserTimeZone);
      if (profileImageFile) {
        payload.append('profile_image', profileImageFile);
      }
      await updateProfile(payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold mb-6">My Profile</h1>

        <div className="mb-6 flex items-center gap-4">
          <div className="h-16 w-16 rounded-xl overflow-hidden border bg-gray-50 flex items-center justify-center">
            {profileImagePreview ? (
              <img
                src={profileImagePreview}
                alt="Profile"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-gray-400 text-sm">No photo</span>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Profile Image</label>
            <input type="file" accept="image/*" onChange={handleFileChange} />
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="w-full input"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="w-full input"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="w-full input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Timezone</label>
            <input
              type="text"
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
              className="w-full input"
            />
          </div>

          <button type="submit" disabled={saving} className="w-full btn-primary disabled:opacity-60">
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserProfile;

